import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import LocalSeeOutlinedIcon from "@mui/icons-material/LocalSeeOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import type { AvatarCropHandle } from "components/avatar-crop";
import ImageUpload from "components/image-upload";
import WebcamPicker from "components/webcam-picker";
import { AI_IMAGE_ENHANCE_FEATURE } from "constants/feature-flags";
import { useEnhanceLogoMutation } from "services/ai";
import { getEnhanceErrorMessage } from "utils/parse-enhance-image-error.util";
import { getFileSizeError } from "utils/validations.utils";

import DynamicWebTwainModal from "../../features/dynamic-web-twain/dynamic-web-twain-modal.component";

type PhotoPickerProps = {
  open: boolean;
  isLoading: boolean;
  hasInitialImage: boolean;
  onClose: () => void;
  onSubmit: (files: File[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  canScan?: boolean;
  folderSettings?: {
    id: number;
    type: "patients";
    folderId: any;
    defaultFolderDestinationPath: string;
  };
  maxSizeLimit?: number;
  /**
   * Opt in to square, circular avatar cropping. Deliberately not a default:
   * insurance card photos are landscape and must not be squared off, and
   * product pictures are not avatars either.
   */
  avatarCrop?: boolean;
  /**
   * Logo surfaces (SN-18765): swaps the webcam/scan/paste entry points for the
   * AI "Optimize Image" action, and requires a staged file before Save.
   */
  enableLogoEnhance?: boolean;
  currentImageSrc?: string;
  title?: string;
};

export type PhotoPickerValues = {
  photos: File[];
  initialValue?: File;
};

const DEFAULT_VALUES: PhotoPickerValues = {
  photos: []
};

function getExtensionFromMime(type: string): string {
  if (type === "image/png") return "png";
  return "jpg";
}

export default function PhotoPicker({
  open,
  isLoading,
  hasInitialImage,
  onClose,
  onSubmit,
  onDelete,
  canScan,
  folderSettings,
  maxSizeLimit = 3 * 1024 * 1024,
  avatarCrop,
  enableLogoEnhance,
  currentImageSrc,
  title = "Photo"
}: PhotoPickerProps) {
  const theme = useTheme();
  const cropRef = useRef<AvatarCropHandle>(null);
  const [step, setStep] = useState<"main" | "webcam">("main");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pasteHint, setPasteHint] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [enhanceLogo, { isLoading: isEnhancing }] = useEnhanceLogoMutation();

  // Kill switch for the AI "Optimize Image" action, whose endpoint
  // (POST /ai/logo/enhance/) ships separately from this frontend. Distinct from
  // `enableLogoEnhance`, which marks a logo SURFACE (no webcam/scan/paste, staged
  // file required before Save) and stays in force either way. (SN-18765)
  const flags = useFlags();
  const isAiEnhanceEnabled = Boolean(flags[AI_IMAGE_ENHANCE_FEATURE]);

  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setPasteHint(null);
      setAiError(null);
    }
  }, [open]);

  const defaultValues: PhotoPickerValues = useMemo(() => {
    return merge({}, DEFAULT_VALUES);
  }, []);

  const formMethods = useForm<PhotoPickerValues>({
    mode: "onChange",
    defaultValues
  });

  useEffect(() => {
    formMethods.reset(defaultValues);
  }, [defaultValues, formMethods]);

  const handleSubmit: SubmitHandler<{ photos: File[] }> = useCallback(
    async (values) => {
      // Re-derive from the live cropper rather than trusting the last
      // auto-applied value, so an adjustment still sitting in the debounce
      // window — or made via an event we don't listen for — is not dropped.
      const cropped = cropRef.current?.applyCrop() ?? null;

      try {
        await onSubmit(cropped ? [cropped] : values.photos);
      } catch {
        // A rejected onSubmit must not close the dialog or discard the staged file —
        // keep both so the user can retry. Callers that reject have already surfaced
        // the failure themselves (the logo desks dispatch an error snackbar), so no
        // message is set here; deliberately NOT errorMessage, which gates the Save
        // button and would make the retry impossible. Mirrors SignaturePicker.
        // Without the catch this rejection escapes react-hook-form's handleSubmit,
        // which re-throws it into a discarded onClick promise. (SN-18765)
        return;
      }

      formMethods.reset();
      onClose();
    },
    [onSubmit, formMethods, onClose]
  );

  const handleDelete = useCallback(async () => {
    try {
      if (onDelete) {
        await onDelete();
      } else {
        await onSubmit?.([]);
      }
    } catch {
      // Same contract as handleSubmit above. ImageUpload's removeFile calls this
      // without awaiting, so a rejection here would surface as an unhandled promise
      // rejection and land in Datadog RUM as an uncaught error. (SN-18765)
      return;
    }

    formMethods.reset();
    onClose();
  }, [onClose, onDelete, onSubmit, formMethods]);

  const handleClose = useCallback(() => {
    onClose();
    setStep("main");
  }, [onClose]);

  const handlePastedFile = useCallback(
    (file: File) => {
      formMethods.setValue("photos", [file]);
    },
    [formMethods]
  );

  useEffect(() => {
    if (!open || enableLogoEnhance) return;

    const handlePaste = (e: ClipboardEvent) => {
      if (!e.clipboardData) return;

      for (const item of Array.from(e.clipboardData.items)) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            const ext = getExtensionFromMime(file.type);
            const namedFile = new File(
              [file],
              `insurance-card-${Date.now()}.${ext}`,
              { type: file.type }
            );
            handlePastedFile(namedFile);
          }
          return;
        }
      }
    };

    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [enableLogoEnhance, open, handlePastedFile]);

  const handleClipboardButtonClick = useCallback(async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find((t: string) =>
          t.startsWith("image/")
        );
        if (imageType) {
          const blob = await item.getType(imageType);
          const ext = getExtensionFromMime(imageType);
          const file = new File([blob], `insurance-card-${Date.now()}.${ext}`, {
            type: imageType
          });
          handlePastedFile(file);
          setPasteHint(null);
          return;
        }
      }
      setPasteHint("No image found on clipboard");
    } catch {
      setPasteHint(
        "Could not access clipboard. Press Ctrl/\u2318+V to paste instead."
      );
    }
  }, [handlePastedFile]);

  const photos = formMethods.watch("photos");

  const handleEnhanceLogo = useCallback(async () => {
    let original = photos?.at(0);

    if (!original && currentImageSrc) {
      try {
        // `no-store` is load-bearing, not an optimisation. The same URL is rendered in an
        // <img> right beside this, which loads in opaque no-CORS mode; the browser can cache
        // that entry and then serve it to this CORS-mode fetch, which fails the CORS check.
        // utils/common.ts documents the mechanism (suspected, not confirmed against the asset
        // host) and #6348 worked around the same class this way. (SN-18765)
        const response = await fetch(currentImageSrc, { cache: "no-store" });
        const blob = await response.blob();
        original = new File([blob], "logo.png", {
          type: blob.type || "image/png"
        });
      } catch {
        if (open) {
          setAiError(
            "Couldn't load the current image. You can upload a new one instead."
          );
        }
        return;
      }
    }

    if (!original) return;

    setAiError(null);

    const formData = new FormData();
    formData.append("image", original);

    try {
      const blob = await enhanceLogo({ formData }).unwrap();
      if (!open) return;

      const improved = new File([blob], "logo.png", { type: "image/png" });
      formMethods.setValue("photos", [improved], { shouldValidate: true });
    } catch (err) {
      if (!open) return;

      setAiError(
        getEnhanceErrorMessage(
          err,
          "Couldn't optimize the image. You can still save the original."
        )
      );
    }
  }, [photos, currentImageSrc, enhanceLogo, formMethods, open]);

  useEffect(() => {
    if (photos?.length) {
      for (const photo of photos) {
        const error = getFileSizeError(photo.size, maxSizeLimit);
        if (error) {
          setErrorMessage(error);
          return;
        }
      }

      setErrorMessage(null);
    } else {
      setErrorMessage(null);
    }
  }, [maxSizeLimit, photos]);

  const showOptimizeButton =
    isAiEnhanceEnabled &&
    enableLogoEnhance &&
    (Boolean(photos?.length) ||
      (hasInitialImage && Boolean(currentImageSrc) && !photos?.length));

  const isSaveDisabled =
    Boolean(errorMessage) ||
    isEnhancing ||
    (enableLogoEnhance && !photos?.length);

  return (
    <Dialog open={open} onClose={handleClose}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          width: "400px",
          p: 4
        }}
      >
        {step === "main" && (
          <>
            <Typography variant="h5" marginBottom={1} component="div">
              {title}
            </Typography>
            {!enableLogoEnhance && (
              <>
                <Typography variant="body1" component="div">
                  Take a picture using Webcam
                </Typography>
                <IconButton
                  onClick={() => setStep("webcam")}
                  size="large"
                  sx={{
                    backgroundColor: theme.palette.grey[100],
                    mt: 2,
                    p: 6
                  }}
                >
                  <LocalSeeOutlinedIcon />
                </IconButton>
                <Divider
                  sx={{
                    width: 150,
                    m: 2
                  }}
                >
                  OR
                </Divider>
              </>
            )}
            {canScan && (
              <>
                <DynamicWebTwainModal
                  multiplePages={false}
                  title="Scan Document"
                  submitButtonText="Save"
                  onSave={(file) => {
                    formMethods.setValue("photos", [file]);
                  }}
                  folderSettings={folderSettings}
                />
                <Divider
                  sx={{
                    width: 150,
                    m: 2
                  }}
                >
                  OR
                </Divider>
              </>
            )}
            <FormProvider {...formMethods}>
              <ImageUpload
                name="photos"
                onRemove={handleDelete}
                hasInitialImage={hasInitialImage}
                avatarCrop={avatarCrop}
                cropRef={cropRef}
                currentImageSrc={currentImageSrc}
                onChange={() => setAiError(null)}
                croppedFileName="logo.png"
                largePreview={enableLogoEnhance}
              />
            </FormProvider>
            {!enableLogoEnhance && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  or press Ctrl/⌘+V to paste
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ContentPasteGoIcon />}
                  onClick={handleClipboardButtonClick}
                  sx={{ mt: 1, textTransform: "none" }}
                  aria-label="Paste from Clipboard"
                >
                  Paste from Clipboard
                </Button>
                {pasteHint && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    {pasteHint}
                  </Typography>
                )}
              </>
            )}
            {Boolean(aiError) && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {aiError}
              </Alert>
            )}
            {showOptimizeButton && (
              <LoadingButton
                type="button"
                variant="outlined"
                size="large"
                loading={isEnhancing}
                startIcon={<AutoFixHighIcon />}
                onClick={handleEnhanceLogo}
                data-testid="optimize-image"
                sx={{ mb: 1, mt: 1 }}
              >
                Optimize Image
              </LoadingButton>
            )}
            <LoadingButton
              variant="contained"
              size="large"
              type="submit"
              loading={isLoading || isEnhancing}
              sx={{
                marginTop: 2
              }}
              disabled={isSaveDisabled}
              onClick={formMethods.handleSubmit(handleSubmit)}
            >
              Save
            </LoadingButton>
          </>
        )}
        {!enableLogoEnhance && step === "webcam" && (
          <Box
            display="flex"
            flexDirection="column"
            sx={{
              minHeight: "500px"
            }}
          >
            <Box display="flex" alignItems="center" marginBottom={2}>
              <IconButton
                sx={{ marginRight: 1 }}
                onClick={() => setStep("main")}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="div" fontWeight={700}>
                Web Camera
              </Typography>
            </Box>
            <WebcamPicker
              container={{ width: 400 }}
              videoConstraints={{
                width: 512,
                height: 512
              }}
              onCapture={(file: File) => {
                setStep("main");
                formMethods.setValue("photos", [file]);
              }}
            />
          </Box>
        )}
        {Boolean(errorMessage) && (
          <Alert severity="error" sx={{ mt: 2, mb: -1 }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    </Dialog>
  );
}
